import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatDateIT } from "@/lib/formatDate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash } from "@phosphor-icons/react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#171717] border border-[#262626] rounded-lg p-3 text-sm shadow-2xl">
      <p className="text-zinc-400 mb-1.5 text-xs">{label}</p>
      {payload.map((entry, i) => {
        const raw = entry.payload?.[`${entry.dataKey}_raw`];
        const meta = entry.payload?.[`${entry.dataKey}_meta`];
        return (
          <p key={`${entry.name}-${i}`} style={{ color: entry.color }} className="font-medium text-xs mb-0.5 last:mb-0">
            <span className="opacity-80">{entry.name}:</span>{" "}
            {raw != null ? `${raw}${meta?.suffix || ""}` : entry.value}
          </p>
        );
      })}
    </div>
  );
};

export default function MeasurementsTab({ clientId }) {
  const [measurements, setMeasurements] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [chartMetrics, setChartMetrics] = useState(["weight_kg"]); // multi-select
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    weight_kg: "",
    body_fat_pct: "",
    chest_cm: "",
    waist_cm: "",
    hips_cm: "",
    biceps_cm: "",
    thighs_cm: "",
    calves_cm: "",
    shoulders_cm: "",
    notes: "",
  });

  const fetchMeasurements = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/measurements`);
      setMeasurements(data);
    } catch {
      // Error handled silently
    }
  }, [clientId]);

  useEffect(() => { fetchMeasurements(); }, [fetchMeasurements]);

  const handleSave = async () => {
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (k !== "date" && k !== "notes" && payload[k] !== "") {
          payload[k] = parseFloat(payload[k]);
        } else if (k !== "date" && k !== "notes" && payload[k] === "") {
          payload[k] = null;
        }
      });
      await axios.post(`${API}/clients/${clientId}/measurements`, payload);
      toast.success("Misurazione aggiunta");
      setShowDialog(false);
      setForm({
        date: new Date().toISOString().split("T")[0],
        weight_kg: "", body_fat_pct: "", chest_cm: "", waist_cm: "", hips_cm: "",
        biceps_cm: "", thighs_cm: "", calves_cm: "", shoulders_cm: "", notes: "",
      });
      fetchMeasurements();
    } catch (err) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/clients/${clientId}/measurements/${id}`);
      toast.success("Misurazione eliminata");
      fetchMeasurements();
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const metricOptions = [
    { key: "weight_kg", label: "Peso", suffix: " kg", color: "#007AFF" },
    { key: "body_fat_pct", label: "% Grasso", suffix: "%", color: "#D4FF00" },
    { key: "chest_cm", label: "Petto", suffix: " cm", color: "#34C759" },
    { key: "waist_cm", label: "Vita", suffix: " cm", color: "#FF3B30" },
    { key: "hips_cm", label: "Fianchi", suffix: " cm", color: "#FF9500" },
    { key: "biceps_cm", label: "Bicipiti", suffix: " cm", color: "#AF52DE" },
    { key: "thighs_cm", label: "Cosce", suffix: " cm", color: "#5AC8FA" },
    { key: "shoulders_cm", label: "Spalle", suffix: " cm", color: "#FFD60A" },
  ];

  const toggleMetric = (key) => {
    setChartMetrics((prev) => {
      if (prev.includes(key)) {
        // Don't allow zero metrics — keep at least one
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      // Limit to 4 overlays for readability
      if (prev.length >= 4) return prev;
      return [...prev, key];
    });
  };

  const isOverlayMode = chartMetrics.length > 1;

  // Build chartData: for each measurement date, include normalized + raw values per selected metric.
  const chartData = (() => {
    const sorted = [...measurements].sort((a, b) => (a.date > b.date ? 1 : -1));
    if (sorted.length === 0) return [];
    // Determine min/max per metric for normalization
    const stats = {};
    chartMetrics.forEach((k) => {
      const values = sorted.map((m) => m[k]).filter((v) => v != null && !Number.isNaN(v));
      if (values.length > 0) {
        const min = Math.min(...values);
        const max = Math.max(...values);
        stats[k] = { min, max, range: max - min || 1 };
      }
    });
    return sorted.map((m) => {
      const row = { date: m.date };
      chartMetrics.forEach((k) => {
        const meta = metricOptions.find((o) => o.key === k);
        const v = m[k];
        if (v != null) {
          row[`${k}_raw`] = v;
          row[`${k}_meta`] = { suffix: meta?.suffix || "" };
          if (isOverlayMode && stats[k]) {
            // Normalize to 0..100 for comparable trends
            row[k] = Math.round(((v - stats[k].min) / stats[k].range) * 100 * 10) / 10;
          } else {
            row[k] = v;
          }
        }
      });
      return row;
    }).filter((d) => chartMetrics.some((k) => d[k] != null));
  })();

  return (
    <div data-testid="measurements-tab">
      {/* Chart */}
      <div className="bg-[#171717] border border-[#262626] rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-bold tracking-tight uppercase text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              ANDAMENTO
            </h3>
            <p className="text-[11px] text-zinc-600 mt-0.5">
              {isOverlayMode
                ? "Confronto multi-metrica · scala normalizzata 0–100"
                : "Tap su più metriche per sovrapporre i trend"}
            </p>
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap mb-5" data-testid="chart-metric-picker-container">
          {metricOptions.map((opt) => {
            const selected = chartMetrics.includes(opt.key);
            return (
              <button
                key={opt.key}
                onClick={() => toggleMetric(opt.key)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all flex items-center gap-1.5 border ${
                  selected
                    ? "text-white border-transparent"
                    : "bg-[#0A0A0A] text-zinc-500 hover:text-zinc-200 border-[#262626]"
                }`}
                style={
                  selected
                    ? { backgroundColor: `${opt.color}20`, borderColor: `${opt.color}66`, color: opt.color }
                    : undefined
                }
                data-testid={`chart-metric-${opt.key}`}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: selected ? opt.color : "#3A3A3A" }}
                />
                {opt.label}
              </button>
            );
          })}
        </div>

        {chartData.length > 1 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis dataKey="date" stroke="#555" tick={{ fontSize: 12 }} />
              <YAxis
                stroke="#555"
                tick={{ fontSize: 12 }}
                domain={isOverlayMode ? [0, 100] : ["auto", "auto"]}
                tickFormatter={isOverlayMode ? (v) => `${v}` : undefined}
                width={isOverlayMode ? 28 : 40}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {chartMetrics.map((k) => {
                const meta = metricOptions.find((o) => o.key === k);
                if (!meta) return null;
                return (
                  <Line
                    key={k}
                    type="monotone"
                    dataKey={k}
                    name={meta.label}
                    stroke={meta.color}
                    strokeWidth={2}
                    dot={{ fill: meta.color, r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-zinc-500 text-sm">
            Aggiungi almeno 2 misurazioni per visualizzare il grafico
          </div>
        )}
      </div>

      {/* Measurements List */}
      <div className="bg-[#171717] border border-[#262626] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold tracking-tight uppercase text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            STORICO MISURAZIONI
          </h3>
          <Button onClick={() => setShowDialog(true)} className="bg-[#007AFF] hover:bg-[#3395FF] text-white" data-testid="add-measurement-button">
            <Plus size={18} className="mr-2" /> Aggiungi
          </Button>
        </div>

        {measurements.length > 0 ? (
          <div className="space-y-3">
            {[...measurements].reverse().map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-[#1A1A1A]" data-testid={`measurement-${m.id}`}>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{formatDateIT(m.date)}</p>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-zinc-400">
                    {m.weight_kg && <span>Peso: <span className="text-[#007AFF]">{m.weight_kg}kg</span></span>}
                    {m.body_fat_pct && <span>Grasso: <span className="text-[#D4FF00]">{m.body_fat_pct}%</span></span>}
                    {m.chest_cm && <span>Petto: {m.chest_cm}cm</span>}
                    {m.waist_cm && <span>Vita: {m.waist_cm}cm</span>}
                    {m.hips_cm && <span>Fianchi: {m.hips_cm}cm</span>}
                    {m.biceps_cm && <span>Bicipiti: {m.biceps_cm}cm</span>}
                    {m.thighs_cm && <span>Cosce: {m.thighs_cm}cm</span>}
                    {m.shoulders_cm && <span>Spalle: {m.shoulders_cm}cm</span>}
                  </div>
                </div>
                <button onClick={() => handleDelete(m.id)} className="text-zinc-600 hover:text-red-400 transition-colors ml-3" data-testid={`delete-measurement-${m.id}`}>
                  <Trash size={18} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center py-8 text-zinc-500 text-sm">Nessuna misurazione registrata</p>
        )}
      </div>

      {/* Add Measurement Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#171717] border-[#262626] text-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid="measurement-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              NUOVA MISURAZIONE
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="measurement-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Peso (kg)</Label>
                <Input type="number" step="0.1" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="measurement-weight" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">% Grasso</Label>
                <Input type="number" step="0.1" value={form.body_fat_pct} onChange={(e) => setForm({ ...form, body_fat_pct: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="measurement-bodyfat" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Petto (cm)</Label>
                <Input type="number" step="0.1" value={form.chest_cm} onChange={(e) => setForm({ ...form, chest_cm: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="measurement-chest" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Vita (cm)</Label>
                <Input type="number" step="0.1" value={form.waist_cm} onChange={(e) => setForm({ ...form, waist_cm: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="measurement-waist" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Fianchi (cm)</Label>
                <Input type="number" step="0.1" value={form.hips_cm} onChange={(e) => setForm({ ...form, hips_cm: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="measurement-hips" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Bicipiti (cm)</Label>
                <Input type="number" step="0.1" value={form.biceps_cm} onChange={(e) => setForm({ ...form, biceps_cm: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="measurement-biceps" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Cosce (cm)</Label>
                <Input type="number" step="0.1" value={form.thighs_cm} onChange={(e) => setForm({ ...form, thighs_cm: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="measurement-thighs" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Spalle (cm)</Label>
                <Input type="number" step="0.1" value={form.shoulders_cm} onChange={(e) => setForm({ ...form, shoulders_cm: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="measurement-shoulders" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A]" data-testid="measurement-cancel">Annulla</Button>
            <Button onClick={handleSave} className="bg-[#007AFF] hover:bg-[#3395FF] text-white" data-testid="measurement-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
